/**
 * Global Authentication State Manager
 *
 * This service provides centralized authentication state management for the entire application.
 * It manages authentication state for multiple SSO providers and provides
 * reactive observables for components to subscribe to authentication changes.
 *
 * Features:
 * - Centralized authentication state management
 * - Reactive state with RxJS observables
 * - Token storage and retrieval
 * - User profile and permissions management
 * - Authentication status tracking
 * - State persistence across browser sessions
 */
import { Injectable, inject } from '@angular/core';
import { AuthenticationState, UserProfile } from '@core/models';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthProvider } from 'src/app/config/sso.config';
import { LoggerService } from '@core/services/logger.service';

// Global application authentication state
export interface GlobalAuthState {
  // Authentication status
  isAuthenticated: boolean;
  currentProvider: AuthProvider | null;

  // User information
  user: UserProfile | null;
  permissions: string[];
  roles: string[];

  // Token management
  accessToken: string | null;
  refreshToken: string | null;
  tokenExpiration: Date | null;

  // Session info
  loginTimestamp: Date | null;
  lastActivity: Date | null;

  // Provider-specific states
  microsoftAuth: AuthenticationState;
  googleAuth: AuthenticationState;
}

@Injectable({
  providedIn: 'root',
})
export class GlobalAuthStateService {
  private readonly logger = inject(LoggerService);

  // ========================================
  // Private State Subjects
  // ========================================

  private globalAuthStateSubject = new BehaviorSubject<GlobalAuthState>({
    isAuthenticated: false,
    currentProvider: null,
    user: null,
    permissions: [],
    roles: [],
    accessToken: null,
    refreshToken: null,
    tokenExpiration: null,
    loginTimestamp: null,
    lastActivity: null,
    microsoftAuth: {
      isAuthenticated: false,
      token: null,
      refreshToken: null,
      user: null,
      tokenExpiration: null,
      loginTimestamp: null,
    },
    googleAuth: {
      isAuthenticated: false,
      token: null,
      refreshToken: null,
      user: null,
      tokenExpiration: null,
      loginTimestamp: null,
    },
  });

  // ========================================
  // Public Observables
  // ========================================

  /**
   * Complete global authentication state
   */
  globalAuthState$: Observable<GlobalAuthState> = this.globalAuthStateSubject.asObservable();

  /**
   * Authentication status observable
   */
  isAuthenticated$: Observable<boolean> = this.globalAuthState$.pipe(
    map((state) => state.isAuthenticated),
  );

  /**
   * Current user profile observable
   */
  user$: Observable<UserProfile | null> = this.globalAuthState$.pipe(map((state) => state.user));

  /**
   * User permissions observable
   */
  permissions$: Observable<string[]> = this.globalAuthState$.pipe(
    map((state) => state.permissions),
  );

  /**
   * User roles observable
   */
  roles$: Observable<string[]> = this.globalAuthState$.pipe(map((state) => state.roles));

  /**
   * Access token observable
   */
  accessToken$: Observable<string | null> = this.globalAuthState$.pipe(
    map((state) => state.accessToken),
  );

  /**
   * Current authentication provider observable
   */
  currentProvider$: Observable<AuthProvider | null> = this.globalAuthState$.pipe(
    map((state) => state.currentProvider),
  );

  /**
   * Microsoft authentication state observable
   */
  microsoftAuthState$: Observable<AuthenticationState> = this.globalAuthState$.pipe(
    map((state) => state.microsoftAuth),
  );

  /**
   * Google authentication state observable
   */
  googleAuthState$: Observable<AuthenticationState> = this.globalAuthState$.pipe(
    map((state) => state.googleAuth),
  );

  // ========================================
  // Synchronous Getters
  // ========================================

  /**
   * Get current global authentication state synchronously
   */
  get currentState(): GlobalAuthState {
    return this.globalAuthStateSubject.value;
  }

  /**
   * Check if user is currently authenticated
   */
  get isAuthenticated(): boolean {
    return this.currentState.isAuthenticated;
  }

  /**
   * Get current user profile
   */
  get currentUser(): UserProfile | null {
    return this.currentState.user;
  }

  /**
   * Get current access token
   */
  get accessToken(): string | null {
    return this.currentState.accessToken;
  }

  /**
   * Get current refresh token
   */
  get refreshToken(): string | null {
    return this.currentState.refreshToken;
  }

  /**
   * Get current authentication provider
   */
  get currentProvider(): AuthProvider | null {
    return this.currentState.currentProvider;
  }

  /**
   * Get user permissions
   */
  get permissions(): string[] {
    return this.currentState.permissions;
  }

  /**
   * Get user roles
   */
  get roles(): string[] {
    return this.currentState.roles;
  }

  // ========================================
  // State Management Methods
  // ========================================

  /**
   * Update Microsoft authentication state
   */
  setMicrosoftAuthState(authState: Partial<AuthenticationState>): void {
    const currentState = this.currentState;
    const updatedMicrosoftAuth = { ...currentState.microsoftAuth, ...authState };

    this.updateGlobalState({
      microsoftAuth: updatedMicrosoftAuth,
      // Update global state if Microsoft auth is becoming authenticated
      ...(authState.isAuthenticated && {
        isAuthenticated: true,
        currentProvider: 'microsoft',
        user: authState.user || currentState.user,
        accessToken: authState.token || currentState.accessToken,
        refreshToken: authState.refreshToken || currentState.refreshToken,
        tokenExpiration: authState.tokenExpiration || currentState.tokenExpiration,
        loginTimestamp: authState.loginTimestamp || new Date(),
        lastActivity: new Date(),
      }),
    });
  }

  /**
   * Update Google authentication state
   */
  setGoogleAuthState(authState: Partial<AuthenticationState>): void {
    const currentState = this.currentState;
    const updatedGoogleAuth = { ...currentState.googleAuth, ...authState };

    this.updateGlobalState({
      googleAuth: updatedGoogleAuth,
      // Update global state if Google auth is becoming authenticated
      ...(authState.isAuthenticated && {
        isAuthenticated: true,
        currentProvider: 'google',
        user: authState.user || currentState.user,
        accessToken: authState.token || currentState.accessToken,
        refreshToken: authState.refreshToken || currentState.refreshToken,
        tokenExpiration: authState.tokenExpiration || currentState.tokenExpiration,
        loginTimestamp: authState.loginTimestamp || new Date(),
        lastActivity: new Date(),
      }),
    });
  }

  /**
   * Update user profile
   */
  setUserProfile(user: UserProfile): void {
    this.updateGlobalState({
      user,
      permissions: user.permissions || [],
      roles: user.roles || [],
    });
  }

  /**
   * Update user permissions
   */
  setPermissions(permissions: string[]): void {
    this.updateGlobalState({ permissions });
  }

  /**
   * Update user roles
   */
  setRoles(roles: string[]): void {
    this.updateGlobalState({ roles });
  }

  /**
   * Update last activity timestamp
   */
  updateActivity(): void {
    this.updateGlobalState({ lastActivity: new Date() });
  }

  /**
   * Clear all authentication state (logout)
   */
  clearAuthState(): void {
    this.globalAuthStateSubject.next({
      isAuthenticated: false,
      currentProvider: null,
      user: null,
      permissions: [],
      roles: [],
      accessToken: null,
      refreshToken: null,
      tokenExpiration: null,
      loginTimestamp: null,
      lastActivity: new Date(),
      microsoftAuth: {
        isAuthenticated: false,
        token: null,
        refreshToken: null,
        user: null,
        tokenExpiration: null,
        loginTimestamp: null,
      },
      googleAuth: {
        isAuthenticated: false,
        token: null,
        refreshToken: null,
        user: null,
        tokenExpiration: null,
        loginTimestamp: null,
      },
    });
  }

  // ========================================
  // Utility Methods
  // ========================================

  /**
   * Check if user has a specific permission
   */
  hasPermission(permission: string): boolean {
    return this.permissions.includes(permission);
  }

  /**
   * Check if user has any of the specified permissions
   */
  hasAnyPermission(permissions: string[]): boolean {
    return permissions.some((permission) => this.hasPermission(permission));
  }

  /**
   * Check if user has all of the specified permissions
   */
  hasAllPermissions(permissions: string[]): boolean {
    return permissions.every((permission) => this.hasPermission(permission));
  }

  /**
   * Check if user has a specific role
   */
  hasRole(role: string): boolean {
    return this.roles.includes(role);
  }

  /**
   * Check if user has any of the specified roles
   */
  hasAnyRole(roles: string[]): boolean {
    return roles.some((role) => this.hasRole(role));
  }

  /**
   * Check if user has all of the specified roles
   */
  hasAllRoles(roles: string[]): boolean {
    return roles.every((role) => this.hasRole(role));
  }

  /**
   * Check if token is expired or about to expire
   */
  isTokenExpired(bufferMinutes: number = 5): boolean {
    const { tokenExpiration } = this.currentState;
    if (!tokenExpiration) return true;

    const bufferMs = bufferMinutes * 60 * 1000;
    return new Date().getTime() + bufferMs >= tokenExpiration.getTime();
  }

  // ========================================
  // Private Helper Methods
  // ========================================

  /**
   * Update global state with partial updates
   */
  private updateGlobalState(updates: Partial<GlobalAuthState>): void {
    const currentState = this.currentState;
    const newState = { ...currentState, ...updates };
    this.globalAuthStateSubject.next(newState);
  }

  constructor() {
    // Initialize state from localStorage if available
    this.initializeFromStorage();
  }

  /**
   * Initialize authentication state from localStorage
   */
  private initializeFromStorage(): void {
    try {
      const storedState = localStorage.getItem('authState');
      if (storedState) {
        const parsedState = JSON.parse(storedState);
        // Convert date strings back to Date objects
        if (parsedState.tokenExpiration) {
          parsedState.tokenExpiration = new Date(parsedState.tokenExpiration);
        }
        if (parsedState.loginTimestamp) {
          parsedState.loginTimestamp = new Date(parsedState.loginTimestamp);
        }
        if (parsedState.lastActivity) {
          parsedState.lastActivity = new Date(parsedState.lastActivity);
        }

        // Check if stored token is still valid
        if (parsedState.tokenExpiration && new Date() < parsedState.tokenExpiration) {
          this.globalAuthStateSubject.next(parsedState);
        }
      }
    } catch (error) {
      this.logger.warn('Failed to load authentication state from storage:', error);
    }
  }

  /**
   * Persist current authentication state to localStorage
   */
  saveToStorage(): void {
    try {
      const currentState = this.currentState;
      localStorage.setItem('authState', JSON.stringify(currentState));
    } catch (error) {
      this.logger.warn('Failed to save authentication state to storage:', error);
    }
  }
}
